import React from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { mediaBreakpoint } from '@root/src/services/responsiveProvider';
import getRoute from '@root/src/services/routeList';
import { textH3 } from '@features/ui/styles/textStyles';
import { ICON_NAME } from '@features/ui/components/Icon';
import {
  selectAccessesOnMyFriends,
  selectCurrentUserDetail,
  selectIsAuthenticated,
} from '@features/users/slices/userSlice';
import { setSubscriptionFor } from '@features/subscriptions/slices/formSubscriptionSlice';
import Button, { BUTTON_VARIANT } from '@features/ui/components/Button';
import InputSelect from '@features/ui-input/components/InputSelect';
import { useGetEventData } from '@features/eventDetail/GetEventData';
import { getQueryEventId } from '@features/eventDetail/utils';

const EventSubscriptionForSelect = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const allAccesses = useSelector(selectAccessesOnMyFriends);
  const userDetail = useSelector(selectCurrentUserDetail);
  const logged = useSelector(selectIsAuthenticated);
  const getEventData = useGetEventData();

  const subscriptionForOptions = logged && allAccesses.map(access => ({
    label: `${access.firstName} ${access.lastName}`,
    value: access._id,
  }));

  const changeSubscriptionFor = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.value;
    const profile = allAccesses.find(access => access._id === id);
    if (profile) {
      dispatch(setSubscriptionFor({ profile }));
      await getEventData.getEventCategories(getQueryEventId(router.query), profile._idMso);
    }
  };

  return logged ? (
    <Container>
      <Label>{t('eventSubscriptionForSelect_label.message')}</Label>
      <StyledSelect
        defaultValue={userDetail._id}
        options={subscriptionForOptions}
        onChange={changeSubscriptionFor}
      />
      <Button
        variant={BUTTON_VARIANT.link}
        text={t('eventSubscriptionForSelect_addFriend.message')}
        leftIcon={ICON_NAME.iconPersonAdd}
        url={getRoute.openModal(router)
          .addFriend()}
        urlScroll={false}
      />
    </Container>
  ) : <></>;
};

export default EventSubscriptionForSelect;

const Container = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 20px 0;

  ${mediaBreakpoint.mobile} {
    flex-direction: column;

    > *:not(:first-child) {
      margin-top: 12px;
    }

    align-items: flex-start;
  }
`;

const Label = styled.h3`
  ${textH3};
  color: var(--color-dark);
  margin-right: 20px;
`;

const StyledSelect = styled(InputSelect)`
  width: 300px;
  margin-right: auto;
`;
